import _defineProperty from "@babel/runtime/helpers/defineProperty";
import * as React from "react";
/**
 * Returns a React component, with a nullable / optional property for each member of TContext.
 * The component will take values supplied via properties and merge those into the consolidated context object specified by reactContextPropertyName.
 * It handles receiving inherited consolidated context and merging the new properties into it.
 * Specifying 'null' for any context property will clear that property from the context.
 * TContext: defines the set of properties stored within the consolidated context.
 * The props of the returned component are Partial<TContext>. Any entry on context is optional.
 * Specified properties get merged into the context object.
 * @param reactContextPropertyName The name of the property to be used for React contextTypes for the consolidated context object.
 * Individual context properties will be properties of the object stored under this top level property name
 * @param displayNameForReactComponent The name that will appear in the React Chrome plugin for the returned provider component
 * @returns A React component that acts as a context provider, with optional React props for each property of TContext.
 */
export function consolidatedContextProviderFactory(ReactContext, displayNameForReactComponent) {
  var _ConsolidatedContextProvider;
  let component = (_ConsolidatedContextProvider = class ConsolidatedContextProvider extends React.Component {
    constructor(...args) {
      super(...args);
      _defineProperty(this, "cachedContext", {});
    }
    reconcileContext(inheritedContext) {
      let contextProps = {
        ...this.props
      }; // any just needed for destructuring today
      delete contextProps.children; // remove react's added children property

      // If any context properties were specified as null, knockout that context property out of the consolidated context
      let propsToClear = [];
      for (let propName in contextProps) {
        if (contextProps.hasOwnProperty(propName) && contextProps[propName] === null) {
          propsToClear.push(propName); // collect names and delete later, don't try to delete while iterating through properties
        }
      }
      let contextForChildren = {
        ...inheritedContext,
        ...contextProps
      };
      for (let propName of propsToClear) {
        delete contextForChildren[propName];
      }
      let contextHasChanged = false;

      // Do a shallow comparison of the new context and the last context we used
      // if they are equivalent then used the cached one to prevent unnecessary rerenders
      let newKeys = Object.keys(contextForChildren);
      let cachedKeys = Object.keys(this.cachedContext);
      if (newKeys.length !== cachedKeys.length) {
        contextHasChanged = true;
      }
      if (!contextHasChanged) {
        for (let i = 0; i < newKeys.length; i++) {
          let newKey = newKeys[i];
          let cachedKey = cachedKeys[i];
          if (contextForChildren[newKey] !== this.cachedContext[newKey] || contextForChildren[cachedKey] !== this.cachedContext[cachedKey]) {
            contextHasChanged = true;
          }
        }
      }
      if (contextHasChanged) {
        this.cachedContext = contextForChildren;
      }
      return this.cachedContext;
    }
    render() {
      return /*#__PURE__*/React.createElement(ReactContext.Consumer, null, inheritedContext => {
        let contextForChildren = this.reconcileContext(inheritedContext);
        return /*#__PURE__*/React.createElement(ReactContext.Provider, {
          value: contextForChildren
        }, this.props.children);
      });
    }
  }, _defineProperty(_ConsolidatedContextProvider, "contextType", ReactContext), _ConsolidatedContextProvider);

  // set name that will be seen in Chrome React plugin for component
  component.displayName = displayNameForReactComponent;
  return component;
}
/**
 * Builds a React Component that provides a single named react context property, which in our case is typically the full consolidated context.
 * Abstracts whether we are using old or new react context implementation
 * Useful for forwarding the consolidated context
 * @param reactContextPropertyName
 * @param displayNameForReactComponent
 */
export function contextProviderFactory(ReactContext, displayNameForReactComponent) {
  var _ContextProvider;
  return _ContextProvider = class ContextProvider extends React.Component {
    render() {
      return /*#__PURE__*/React.createElement(ReactContext.Provider, {
        value: this.props.context
      }, this.props.children);
    }
  }, _defineProperty(_ContextProvider, "displayName", displayNameForReactComponent), _ContextProvider;
}

/* There's a big problem here. For mobx based components the consumer component must be an observer to re-render reliably.
But if this is an observer non-mobx components don't re-render reliably.
For now the HOC approach below is the recommended way to do things.
See http://confluence.abinitio.com:8090/display/AISDK/React+16+Context+API
*/

// export interface ConsolidatedContextConsumerProps<TContext>
// {
//   renderContent: (context: Partial<TContext>) => React.ReactNode;
// }
//
// /* Build a React-16.2 style consumer component, but with typesafety via renderContent property rather than child */
// export function consolidatedContextConsumerFactory<TContext>(reactContextPropertyName: string, displayNameForReactComponent: string)
// //: {new (props: any): React.Component<ConsolidatedContextConsumerProps<TContext>>}
// {
//   let reactContextTypes: any = {};
//   reactContextTypes[reactContextPropertyName] = PropTypes.object;
//
//   /* There's a big problem here. For mobx based components this must be an observer to re-render reliably.
//       But if this is an observer non-mobx components don't re-render reliably.
//       For now the HOC approach below is the recommended way to do things.
//    */
//   //let component = observer(class ConsolidatedContextConsumer extends React.Component<ConsolidatedContextConsumerProps<TContext>>
//   let component = (class ConsolidatedContextConsumer extends React.Component<ConsolidatedContextConsumerProps<TContext>>
//   {
//     render()
//     {
//       return this.props.renderContent(this.context[reactContextPropertyName] || {});
//     }
//
//     static contextTypes = reactContextTypes;
//   });
//
//   // set name that will be seen in Chrome React plugin for component
//   (component as any).displayName = displayNameForReactComponent;
//   return component;
// }
//
// export interface ConsolidatedContextSinglePropertyConsumerProps<TProperty>
// {
//   renderContent: (contextValue: TProperty | undefined) => React.ReactNode;
// }
//
// export function consolidatedContextSinglePropertyConsumerFactory<TContext, TProperty>(reactContextPropertyName: string, displayNameForReactComponent: string,
//   getPropertyFromContext: (context: Partial<TContext>) => TProperty | undefined )
// {
//   let reactContextTypes: any = {};
//   reactContextTypes[reactContextPropertyName] = PropTypes.object;
//
//   let component = observer(class ConsolidatedContextSinglePropertyConsumer extends React.Component<ConsolidatedContextSinglePropertyConsumerProps<TProperty>>
//   {
//     render()
//     {
//       return this.props.renderContent(getPropertyFromContext(this.context[reactContextPropertyName] || {}));
//     }
//
//     static contextTypes = reactContextTypes;
//   });
//
//   // set name that will be seen in Chrome React plugin for component
//   (component as any).displayName = displayNameForReactComponent;
//   return component;
// }

/**
 * For consuming context, this creates a function that will wrap a React Component and return a new component that
 * connects to React context, and then renders the wrapped component with additional injected properties containing the context values.
 * @param reactContextPropertyName The name of the React context property used to hold the consolidated context
 * @param displayNameForReactComponent The React Chrome debugger will display this name for the wrapper component
 * @param getInjectedPropsFromConsolidatedContext A function that takes the full consolidated context, with all possible
 * context properties that have been consolidated into a single react context, and returns the set of injected properties that will
 * be supplied to the wrapped React component. Typically this is a very dumb and seemingly redundant piece of code, although it
 * could potentially remap names, although that might be confusing.
 */
export function consolidatedContextHocFactory(ReactContext, displayNameForReactComponent, getInjectedPropsFromConsolidatedContext) {
  return function (base) {
    var _Class;
    let component = (_Class = class component extends React.Component {
      render() {
        return /*#__PURE__*/React.createElement(ReactContext.Consumer, null, context => {
          let injectedProps = getInjectedPropsFromConsolidatedContext(context || {});
          return /*#__PURE__*/React.createElement(base, {
            ...this.props,
            ...injectedProps
          });
        });
      }
    }, _defineProperty(_Class, "contextType", ReactContext), _Class);
    component.displayName = displayNameForReactComponent;
    return component;
  };
}
export function contextHocFactory(ReactContext, displayNameForReactComponent) {
  return function (base) {
    var _ContextProvider2;
    return _ContextProvider2 = class ContextProvider extends React.Component {
      render() {
        return /*#__PURE__*/React.createElement(ReactContext.Consumer, null, context => {
          return /*#__PURE__*/React.createElement(base, {
            ...this.props,
            context: context
          });
        });
      }
    }, _defineProperty(_ContextProvider2, "contextType", ReactContext), _defineProperty(_ContextProvider2, "displayName", displayNameForReactComponent), _ContextProvider2;
  };
}
export function consolidatedContextHocFactoryFC(ReactContext, displayNameForReactComponent, getInjectedPropsFromConsolidatedContext) {
  return function (base) {
    let component = props => {
      return /*#__PURE__*/React.createElement(ReactContext.Consumer, null, context => {
        let injectedProps = getInjectedPropsFromConsolidatedContext(context || {});
        return /*#__PURE__*/React.createElement(base, {
          ...props,
          ...injectedProps,
          context: context
        });
      });
    };
    component.displayName = displayNameForReactComponent;
    return component;
  };
}
//# sourceMappingURL=ConsolidatedContextComponentFactories.js.map
